import React, { useState, useEffect } from 'react'
import { Card, Typography, Button, message, Spin } from 'antd'
import { FaPlay, FaThumbsUp, FaThumbsDown } from 'react-icons/fa'
import styled from 'styled-components'
import axios from 'axios'
import ShortsPlayer from '../../../Library/internal/DocumentManagement/ShortsPlayer'
import { config } from '../../../../constants'
import { getToken, getUser } from '../../../../services/api/user'
import history from '../../../../history'
import { Spinner } from '../../../../components'
import { useTranslation } from 'react-i18next'

const { Title } = Typography

const StyledCard = styled(Card)`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .ant-card-body {
    padding: 12px;
  }
`

const VideoThumbnail = styled.div`
  position: relative;
  width: 100%;
  height: 210px;
  background: linear-gradient(135deg, #292a2fff 0%, #403b45ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
  cursor: pointer;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const PlayIconWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
`

const PlayIcon = styled(FaPlay)`
  color: white;
  font-size: 48px;
  opacity: 0.8;
  transition: opacity 0.3s ease;

  ${StyledCard}:hover & {
    opacity: 1;
  }
`

const VideoIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: transparent;
`

const ClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  cursor: pointer;
`

const VideoLoader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props =>
    props.gradient || 'linear-gradient(135deg, #27282bff 0%, #3c3a3eff 100%)'};
  z-index: 3;
`

const StatusBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: ${props => {
    switch (props.status) {
      case 'approved':
        return '#52c41a'
      case 'rejected':
        return '#ff4d4f'
      case 'pending':
        return '#faad14'
      default:
        return '#52c41a'
    }
  }};
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  z-index: 4;
`

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const ChannelTag = styled.div`
  display: inline-block;
  background: #1890ff;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`

const VideoTitle = styled(Title)`
  &.ant-typography {
    margin-bottom: 8px !important;
    color: #262626 !important;
    font-size: 16px !important;
    font-weight: 600 !important;
    line-height: 1.3 !important;
  }
`

const DateInfo = styled.div`
  color: #8c8c8c;
  font-size: 12px;
  margin-bottom: 16px;
`

const BottomRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 12px;
`

const AuthorInfo = styled.div`
  color: #1890ff;
  font-size: 12px;
  font-weight: 500;
`

const EngagementStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
`

const StatButton = styled(Button)`
  &.ant-btn {
    border: none;
    background: transparent;
    color: #8c8c8c;
    padding: 0;
    height: auto;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;

    &:hover,
    &:focus {
      background: transparent;
    }

    &.liked {
      color: #1890ff;
    }
    &.disliked {
      color: #ff4d4f;
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  margin-top: 26px;
`

const HeaderTitle = styled(Title)`
  &.ant-typography {
    color: #272626ff !important;
    font-size: 1.8rem !important;
    font-weight: 700 !important;
    display: flex;
    align-items: center;
    gap: 8px;
    @media (max-width: 768px) {
      font-size: 1.2rem !important;
      margin: 0;
    }
  }
`

const Dot = styled.span`
  font-size: 35px;
  line-height: 0;
  margin-top: -6px;
`

const SeeMoreLink = styled.a`
  color: #1890ff;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  @media (max-width: 768px) {
    font-size: 1.2rem !important;
  }
  &:hover {
    color: #40a9ff;
  }
`

const ScrollableContainer = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding-bottom: 16px;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`

const CardWrapper = styled.div`
  display: inline-block;
  width: 220px;
  margin-right: 16px;
  vertical-align: top;
  white-space: normal;

  &:last-child {
    margin-right: 0;
  }
`

const ShortsCard = ({ data, loading }) => {
  const [enableModal, setEnableModal] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [shlToken, setShlToken] = useState(null)
  const [userData, setUserData] = useState(null)

  const [hoveredVideo, setHoveredVideo] = useState(null)
  const [loadingVideo, setLoadingVideo] = useState(null)

  const [videoReactions, setVideoReactions] = useState({})
  const [loadingLikes, setLoadingLikes] = useState({})
  const [loadingDislikes, setLoadingDislikes] = useState({})
  const [videoCounts, setVideoCounts] = useState({})
  const [thumbnails, setThumbnails] = useState({})

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await getToken()
        const user = await getUser()
        setShlToken(token)
        setUserData(user)
      } catch (err) {
        console.error('Error fetching SHL token:', err)
      }
    }
    fetchToken()
  }, [])

  // ✅ Fetch thumbnails for each video
  useEffect(() => {
    async function fetchThumbnails() {
      const newThumbs = {}
      for (const video of data || []) {
        const match = /vimeo.*\/(\d+)/.exec(video.link)
        if (!match) continue
        const videoId = match[1]
        try {
          const res = await fetch(
            `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&width=200&height=640`
          )
          const json = await res.json()
          if (json.thumbnail_url) {
            newThumbs[video.tip.id] = json.thumbnail_url
          }
        } catch (err) {
          console.error('Thumbnail fetch error:', err)
        }
      }
      setThumbnails(prev => ({ ...prev, ...newThumbs }))
    }
    fetchThumbnails()
  }, [data])

  const handleMouseEnter = videoId => {
    setHoveredVideo(videoId)
    setLoadingVideo(videoId)
  }
  const handleMouseLeave = () => {
    setHoveredVideo(null)
    setLoadingVideo(null)
  }
  const handleVideoLoad = () => {
    setLoadingVideo(null)
  }

  const handleReaction = async (videoId, reaction) => {
    if (reaction === 'like') {
      setLoadingLikes(prev => ({ ...prev, [videoId]: true }))
    } else {
      setLoadingDislikes(prev => ({ ...prev, [videoId]: true }))
    }
    try {
      const response = await axios.patch(
        `${config.baseURL}library/tip/${videoId}/react`,
        { reaction },
        { headers: { Authorization: `Bearer ${shlToken}` } }
      )
      if (response.status === 200 && response.data.success) {
        setVideoReactions(prev => ({ ...prev, [videoId]: reaction }))
        setVideoCounts(prev => ({
          ...prev,
          [videoId]: {
            likes_count: response.data.likes_count,
            dislikes_count: response.data.dislikes_count
          }
        }))
      }
    } catch (error) {
      console.error('Error reacting to video:', error)
      if (error.response?.status === 409) {
        setVideoReactions(prev => ({ ...prev, [videoId]: reaction }))
      }
    } finally {
      if (reaction === 'like') {
        setLoadingLikes(prev => ({ ...prev, [videoId]: false }))
      } else {
        setLoadingDislikes(prev => ({ ...prev, [videoId]: false }))
      }
    }
  }

  const handleLike = videoId => handleReaction(videoId, 'like')
  const handleDislike = videoId => handleReaction(videoId, 'dislike')

  const formatDate = dateString => {
    const date = new Date(dateString)
    return `${date.getDate()} ${date.toLocaleString('en-US', {
      month: 'short'
    })}, ${date.getFullYear()}`
  }

  const handleCardClick = video => {
    setSelectedVideo(video)
    setEnableModal(true)
  }

  const handleNextVideo = () => {
    if (!selectedVideo || !data?.length) return
    const currentIndex = data.findIndex(v => v.id === selectedVideo.id)
    if (currentIndex !== -1 && currentIndex < data.length - 1) {
      setSelectedVideo(data[currentIndex + 1])
    }
  }
  const handlePrevVideo = () => {
    if (!selectedVideo || !data?.length) return
    const currentIndex = data.findIndex(v => v.id === selectedVideo.id)
    if (currentIndex > 0) {
      setSelectedVideo(data[currentIndex - 1])
    }
  }

  const navigateTo = () => history.push(`/training/shorts`)
  const { t } = useTranslation()

  return (
    <div>
      <HeaderSection>
        <HeaderTitle level={8}>
          <Dot>●</Dot>
          {t('v5:my_video_shorts')}
        </HeaderTitle>
        {!loading && data?.length > 0 && (
          <SeeMoreLink onClick={navigateTo}>
            {t('v5:see_more')} <span style={{ fontWeight: '900' }}>{'>'}</span>
          </SeeMoreLink>
        )}
      </HeaderSection>

      <ScrollableContainer>
        {loading && <Spinner size="small" />}
        {!loading && data?.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 0',
              color: '#888',
              fontSize: '16px',
              fontWeight: 500
            }}
          >
            {t('v5:no_shorts_found')}
          </div>
        )}
        {!loading &&
          data?.length > 0 &&
          data.map(video => {
            const videoId = video.tip.id
            const hasReacted = videoReactions[videoId]
            const isLikeLoading = loadingLikes[videoId] || false
            const isDislikeLoading = loadingDislikes[videoId] || false
            const currentCounts = videoCounts[videoId] || {
              likes_count: video.tip.likes_count,
              dislikes_count: video.tip.dislikes_count
            }

            return (
              <CardWrapper key={videoId} className="shorts-card-wrap">
                <StyledCard
                  hoverable
                  cover={
                    <VideoThumbnail
                      style={{ background: video.gradient }}
                      onClick={() => handleCardClick(video)}
                      onMouseEnter={() => handleMouseEnter(videoId)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {hoveredVideo === videoId ? (
                        <>
                          {loadingVideo === videoId && (
                            <VideoLoader gradient={video.gradient}>
                              <Spin size="large" style={{ color: '#fff' }} />
                            </VideoLoader>
                          )}
                          <div
                            style={{
                              position: 'relative',
                              width: '100%',
                              height: '210px', // ✅ ensure iframe has height
                              overflow: 'hidden'
                            }}
                          >
                            <VideoIframe
                              src={`${video.link}?autoplay=1&muted=0&controls=0&background=1`}
                              frameBorder="0"
                              allow="autoplay;"
                              allowFullScreen
                              onLoad={handleVideoLoad}
                            />
                            <ClickOverlay
                              onClick={() => handleCardClick(video)}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          {thumbnails[videoId] ? (
                            <div
                              style={{
                                position: 'relative',
                                width: '100%',
                                height: '210px', // ✅ match thumbnail height
                                backgroundColor: '#000',
                                borderRadius: 2,
                                overflow: 'hidden'
                              }}
                            >
                              <img
                                src={thumbnails[videoId]}
                                alt={video.title}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </div>
                          ) : (
                            <PlayIconWrapper>
                              <PlayIcon />
                            </PlayIconWrapper>
                          )}
                        </>
                      )}
                      <StatusBadge status={video.tip.status}>
                        {video.tip.status}
                      </StatusBadge>
                    </VideoThumbnail>
                  }
                >
                  <TopRow>
                    <ChannelTag>{t('v5:shorts')}</ChannelTag>
                  </TopRow>
                  <h3
                    onClick={() => handleCardClick(video)}
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: '#333',
                      marginBottom: 8,
                      lineHeight: 1.3,
                      minHeight: 42,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      margin: '0 0 8px 0'
                    }}
                  >
                    {video.title || video.filename || 'Untitled Video'}
                  </h3>
                  <DateInfo onClick={() => handleCardClick(video)}>
                    {formatDate(video.created_at)}
                  </DateInfo>
                  <BottomRow>
                    <AuthorInfo>
                      By{' '}
                      {video.creator?.username ||
                        video.creator?.name ||
                        'Unknown'}
                    </AuthorInfo>
                  </BottomRow>
                  <EngagementStats>
                    <StatButton
                      className={hasReacted === 'like' ? 'liked' : ''}
                      onClick={() => handleLike(videoId)}
                      disabled={isLikeLoading || isDislikeLoading}
                      loading={isLikeLoading}
                    >
                      <FaThumbsUp />
                      {currentCounts?.likes_count}
                    </StatButton>
                    <StatButton
                      className={hasReacted === 'dislike' ? 'disliked' : ''}
                      onClick={() => handleDislike(videoId)}
                      disabled={isLikeLoading || isDislikeLoading}
                      loading={isDislikeLoading}
                    >
                      <FaThumbsDown />
                      {currentCounts.dislikes_count}
                    </StatButton>
                  </EngagementStats>
                </StyledCard>
              </CardWrapper>
            )
          })}

        {enableModal && selectedVideo && (
          <ShortsPlayer
            defaultQuery={null}
            isAdmin={false}
            visible={enableModal}
            item={selectedVideo}
            shlToken={shlToken}
            onCancelTips={() => setEnableModal(false)}
            onNext={handleNextVideo}
            onPrev={handlePrevVideo}
            hasNext={true}
            hasPrev={true}
            hideButton={true}
            userData={userData}
          />
        )}
      </ScrollableContainer>
    </div>
  )
}

export default ShortsCard
